import { useState, useEffect, useCallback, useRef } from 'react'
import secondStepApiV4 from 'lib/api/secondStepApiV4'

const SCHOOL_IS_NOT_LISTED_OPTION = {
  id: 0,
  companyName: 'My school is not listed',
}

export const useLocationData = (
  country,
  region,
  city,
  hasRegionData,
  onFormChange,
) => {
  const [data, setData] = useState({
    countries: [],
    regions: [],
    cities: [],
    schools: [SCHOOL_IS_NOT_LISTED_OPTION],
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Use ref to avoid dependency issues
  const onFormChangeRef = useRef(onFormChange)
  onFormChangeRef.current = onFormChange

  // Helper function to update data and notify parent
  const updateDataAndNotify = useCallback(
    (updates, notificationType = null, notificationData = null) => {
      setData(prev => ({ ...prev, ...updates }))

      if (notificationType && onFormChangeRef.current) {
        onFormChangeRef.current(
          { target: { value: notificationData } },
          notificationType,
        )
      }
    },
    [],
  )

  // Generic fetch function with error handling
  const fetchWithErrorHandling = useCallback(
    async (fetchFn, onSuccess, onError) => {
      setLoading(true)
      setError(null)

      try {
        const result = await fetchFn()
        await onSuccess(result)
      } catch (err) {
        setError(err)
        await onError(err)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  // Fetch countries (only once on mount)
  useEffect(() => {
    fetchWithErrorHandling(
      () => secondStepApiV4.getCountries(),
      result => updateDataAndNotify({ countries: result.data || [] }),
      () => updateDataAndNotify({ countries: [] }),
    )
  }, [fetchWithErrorHandling, updateDataAndNotify])

  // Fetch regions when country changes
  useEffect(() => {
    if (!country) {
      updateDataAndNotify({ regions: [] }, 'regionData', [])
      return
    }

    fetchWithErrorHandling(
      () => secondStepApiV4.getRegions(country),
      result => {
        const regionsData = result.data || []
        updateDataAndNotify({ regions: regionsData }, 'regionData', regionsData)
      },
      () => updateDataAndNotify({ regions: [] }, 'regionData', []),
    )
  }, [country, fetchWithErrorHandling, updateDataAndNotify])

  // Fetch cities when country/region changes
  useEffect(() => {
    if (!country) {
      updateDataAndNotify({ cities: [] })
      return
    }

    const fetchFn = region
      ? () => secondStepApiV4.getCitiesWithRegion(country, region)
      : () => secondStepApiV4.getCitiesWithoutRegion(country)

    fetchWithErrorHandling(
      fetchFn,
      result => {
        const citiesData = result.data || []
        // If we have a city in siteData but it's not in the API response, add it
        const citiesWithCurrent = [...citiesData]
        if (city && !citiesData.includes(city)) {
          citiesWithCurrent.unshift(city)
        }
        updateDataAndNotify({ cities: citiesWithCurrent })
      },
      () => updateDataAndNotify({ cities: [] }),
    )
  }, [country, region, city, fetchWithErrorHandling, updateDataAndNotify])

  // Fetch schools when city/country/region changes
  useEffect(() => {
    if (!city || !country) {
      const fallbackSchools = [SCHOOL_IS_NOT_LISTED_OPTION]
      updateDataAndNotify({ schools: fallbackSchools })
      return
    }

    const fetchFn =
      hasRegionData && region
        ? () => secondStepApiV4.getNSCustomersWithRegion(city, country, region)
        : () => secondStepApiV4.getNSCustomersWithoutRegion(city, country)

    fetchWithErrorHandling(
      fetchFn,
      result => {
        const schoolsData = result.data || []
        // Add "My school is not listed" option at the beginning (if not already present)
        // If API already has an option with id === 0, replace it with our local version
        const schoolsWithoutUnlisted = schoolsData.filter(
          school => school.id !== 0,
        )
        const schoolsWithUnlisted = [
          SCHOOL_IS_NOT_LISTED_OPTION,
          ...schoolsWithoutUnlisted,
        ]

        updateDataAndNotify(
          { schools: schoolsWithUnlisted },
          'netSuiteData',
          schoolsWithUnlisted,
        )
      },
      () => {
        const fallbackSchools = [SCHOOL_IS_NOT_LISTED_OPTION]
        updateDataAndNotify(
          { schools: fallbackSchools },
          'netSuiteData',
          fallbackSchools,
        )
      },
    )
  }, [
    city,
    country,
    region,
    hasRegionData,
    fetchWithErrorHandling,
    updateDataAndNotify,
  ])

  return {
    ...data,
    loading,
    error,
  }
}
